import React, {useMemo, useCallback, useState, useRef, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import ProductCard from '../ProductCard/ProductCard';
import ProductSkeleton from '../ProductSkeleton/ProductSkeleton';
import {useProductCardProps} from '../../../hooks/useProductCardProps';
import './ProductList.css';
import { logSeenEventView, logEventView } from "../../../services/googleAnalytics";

const ProductList = ({
		products = [],
		onProductClick,
		loading = false,
		cartType = 'normal',
		deviceType = 'desktop',
		currency = '$',
		showSkeletonCount = 12,
		onCartClick,
		isLoadMore = false
	}) => {
	const history = useHistory();
	const itemsRefs = useRef({});
	const [seenProductIds, setSeenProductIds] = useState([]);

	// Handle product click
	const handleProductClick = useCallback((refId) => {
		if (onProductClick) {
			onProductClick(refId);
		} else {
			history.push(`/product/${refId}`);
		}
	}, [onProductClick, history]);

	// Map product data to card props
	const mapProductToCardProps = useCallback((product) => {
		const discountPercent = product.originalPrice > 0 && product.price < product.originalPrice
			? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
			: 0;

		const salePrice = product.originalPrice > 0 ? product.originalPrice : product.price;

		return {
			title: product.name || '',
			image: product.imageResized || product.image || '',
			weight: product.weight || '',
			salePrice,
			discountInPercent: product.discountPercent || discountPercent,
			data: {
				item_id: product.id,
				...product,
				is_shop_open: product.shopOpen,
				country: product.country,
				countries: product.country ? [product.country] : [],
			},
			brand: product.brand,
			outOfStock: !product.inStock,
			promo: product.isPromo ? 1 : 0,
			promoPrice: product.isPromo ? product.price : 0,
			productCode: product.barCode || product.refId || product.id?.toString() || '',
			isSameDay: product.sameDayDelivery && product.inStock,
			shopOpen: product.shopOpen,
			availableTomorrow: product.availableTomorrow,
			isFavorite: product.isFavorite || 0,
			description: product.description || '',
			onClick: () => handleProductClick(product.refId),
			onCartClick,
		};
	}, [handleProductClick, onCartClick]);

	// Render skeleton cards
	const skeletonCards = useMemo(() => (
		[...Array(showSkeletonCount)].map((_, index) => (
			<ProductSkeleton key={`skeleton-${index}`} cartType={cartType}/>
		))
	), [showSkeletonCount, cartType]);

	const loadMoreSkeletons = useMemo(() => (
		[...Array(20)].map((_, index) => (
			<ProductSkeleton key={`load-more-${index}`} cartType={cartType}/>
		))
	), [cartType]);

	// Track product visibility with IntersectionObserver
	useEffect(() => {
		if (!products.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const id = entry.target.dataset.id;
						if (id) {
							setSeenProductIds(prev => {
								if (prev.includes(id)) return prev;
								return [...prev, id];
							});
							observer.unobserve(entry.target);
						}
					}
				});
			},
			{
				root: null,
				rootMargin: "0px",
				threshold: 0.1
			}
		);

		Object.values(itemsRefs.current).forEach(node => {
			if (node) observer.observe(node);
		});

		return () => observer.disconnect();
	}, [products]);

	let url = window.location.pathname;
	let newUrl = url.split("/").filter(Boolean);
	
	// Log seen events based on device type
	useEffect(() => {
		if (seenProductIds.length === 0) return;
		
		// Get threshold based on device type
		let threshold = 16; // desktop default
		if (deviceType === 'mobile' || deviceType.mobile) {
			threshold = 4;
		} else if (deviceType === 'tablet' || deviceType.tablet) {
			threshold = 8;
		}

		// Log when we reach the threshold OR when all products have been seen
		const shouldLog = 
			seenProductIds.length % threshold === 0 || // Reached threshold
			seenProductIds.length === products.length; // All products seen
		
		if (shouldLog) {
			logEventView(newUrl[0], '', seenProductIds.length, JSON.stringify(seenProductIds));
		}
	}, [seenProductIds, deviceType, products.length]);

	// Show skeleton on initial load
	if (loading && products.length === 0) {
		return (
			<div className="product-list">
				<div className="product-list__grid">{skeletonCards}</div>
			</div>
		);
	}

	// Empty state
	if (!loading && products.length === 0) {
		return null;
	}

	// Main render
	return (
		<div className="product-list">
			<div className="product-list__grid">
				{products.map((product) => (
					<div
						key={product.id || product.refId}
						data-id={product.id}
						ref={el => itemsRefs.current[product.id] = el}
					>
						<ProductCard
							{...mapProductToCardProps(product)}
						/>
					</div>
				))}

				{/* Load more skeletons */}
				{isLoadMore && loadMoreSkeletons}
			</div>
		</div>
	);
};

export default React.memo(ProductList);